@file:OptIn(kotlin.concurrent.atomics.ExperimentalAtomicApi::class)

package com.dotpx.ambient

import kotlin.concurrent.atomics.AtomicLong
import kotlin.concurrent.atomics.AtomicLongArray
import kotlin.math.roundToInt

/**
 * Fixed-size seqlock telemetry. The audio thread only quantizes and atomically
 * stores primitive words; model/list allocation happens when the UI reads.
 */
internal class PackedVisualTelemetry {
    private val revision = AtomicLong(0L)
    private val signals = AtomicLongArray(SIGNAL_COUNT + 5)
    private val transitionGeneration = AtomicLong(0L)
    private val transitionProgress = AtomicLong(1f.toBits().toLong())

    internal fun storeTransition(generation: Long, progress: Float) {
        transitionGeneration.store(generation)
        transitionProgress.store(progress.coerceIn(0f, 1f).toBits().toLong())
    }

    internal fun beginPublish() {
        revision.fetchAndAdd(1L)
    }

    internal fun store(
        index: Int,
        sound: AmbienceVisualSound,
        weight: Float,
        convergence: Float,
        energy: Float,
    ) {
        signals.storeAt(index, pack(sound, weight, convergence, energy))
    }

    internal fun endPublish() {
        revision.fetchAndAdd(1L)
    }

    internal fun snapshot(): AmbienceVisualFrame {
        val packedSignals = LongArray(SIGNAL_COUNT + 5)
        var generation: Long
        var progress: Float
        while (true) {
            val revisionBefore = revision.load()
            if (revisionBefore and 1L != 0L) continue
            var index = 0
            while (index < packedSignals.size) {
                packedSignals[index] = signals.loadAt(index)
                index++
            }
            generation = transitionGeneration.load()
            progress = Float.fromBits(transitionProgress.load().toInt())
            if (revisionBefore == revision.load()) break
        }

        val decoded = ArrayList<AmbienceVisualSignal>(SIGNAL_COUNT)
        var index = 0
        while (index < SIGNAL_COUNT) {
            decoded.add(unpack(packedSignals[index]))
            index++
        }
        val lightSignals = ArrayList<AmbienceVisualSignal>(SIGNAL_COUNT)
        for (baseIndex in SIGNAL_COUNT until packedSignals.size) {
            val signal = unpack(packedSignals[baseIndex])
            if (!signal.sound.isTransient) lightSignals.add(signal)
        }
        lightSignals.addAll(decoded.filter { it.sound.isTransient })
        return AmbienceVisualFrame(decoded, lightSignals, generation, progress)
    }

    private fun pack(
        sound: AmbienceVisualSound,
        weight: Float,
        convergence: Float,
        energy: Float,
    ): Long = sound.ordinal.toLong() or
        (quantize(weight).toLong() shl 8) or
        (quantize(convergence).toLong() shl 24) or
        (quantize(energy).toLong() shl 40)

    private fun unpack(packed: Long): AmbienceVisualSignal {
        val ordinal = (packed and 0xFFL).toInt()
        return AmbienceVisualSignal(
            sound = AmbienceVisualSound.entries.getOrElse(ordinal) { AmbienceVisualSound.Room },
            weight = dequantize(((packed ushr 8) and 0xFFFFL).toInt()),
            convergence = dequantize(((packed ushr 24) and 0xFFFFL).toInt()),
            energy = dequantize(((packed ushr 40) and 0xFFFFL).toInt()),
        )
    }

    private fun quantize(value: Float): Int =
        (value.coerceIn(0f, 1f) * QUANTIZATION_MAX.toFloat()).roundToInt()

    private fun dequantize(value: Int): Float = value.toFloat() / QUANTIZATION_MAX.toFloat()

    internal companion object {
        const val SIGNAL_COUNT = 10
        private const val QUANTIZATION_MAX = 65_535
    }
}
